import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';
import type { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { AttachmentRecord } from '../attachments-panel/attachments-panel.component';
import { ModalComponent } from '../modal/modal.component';
import { AttachmentViewerService, type AttachmentViewerHost } from './attachment-viewer.service';

export type PreviewKind = 'pdf' | 'image' | 'text' | 'unsupported';
type ViewerStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Beyond this, a text preview stops being useful and starts being a memory hog. */
const TEXT_PREVIEW_LIMIT = 512 * 1024;

const TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  txt: 'text/plain',
  csv: 'text/csv',
};

/**
 * What the browser can render inline. Legacy uploads copied with AzCopy carry
 * `application/octet-stream`, so the extension decides when the type says nothing.
 */
export function previewKindFor(contentType: string, filename: string): PreviewKind {
  const declared = (contentType || '').split(';', 1)[0]!.trim().toLowerCase();
  const extension = filename.match(/\.([A-Za-z0-9]{1,8})$/)?.[1]?.toLowerCase() ?? '';
  const type = !declared || declared === 'application/octet-stream' ? TYPE_BY_EXTENSION[extension] ?? '' : declared;
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (type === 'text/plain' || type === 'text/csv') return 'text';
  return 'unsupported';
}

export function viewerErrorMessage(status: number): string {
  switch (status) {
    case 401: return 'Your session has expired. Sign in again to view this file.';
    case 403: return "You don't have permission to view this file.";
    case 404: return 'This file could not be found. It may have been removed.';
    case 503: return 'File storage is unavailable right now. Try again in a few minutes.';
    case 0: return 'Could not reach the server.';
    default: return 'The file could not be loaded.';
  }
}

function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path) || path.startsWith('blob:') || path.startsWith('data:')) return path;
  return `${environment.apiUrl}${path}`;
}

async function readTextPreview(blob: Blob): Promise<string> {
  const truncated = blob.size > TEXT_PREVIEW_LIMIT;
  const text = await (truncated ? blob.slice(0, TEXT_PREVIEW_LIMIT) : blob).text();
  return truncated ? `${text}\n\n… preview truncated — download the file to see the rest.` : text;
}

/**
 * Shows an attachment inside the app instead of sending the user off to a
 * download. The file is fetched through the authenticated API (so the server's
 * per-record permission check still applies) and rendered from a local object
 * URL: PDFs in a frame, images directly, plain text as text. Anything else
 * offers a download. Mounted once from the app root and driven through
 * `AttachmentViewerService`.
 */
@Component({
  selector: 'mac-attachment-viewer',
  imports: [ModalComponent],
  template: `
    <mac-modal #modal [title]="attachment()?.filename ?? 'Attachment'" icon="paperclip" size="xl" (closed)="onClosed()">
      <div class="modal-body viewer-body" [class.viewer-body--frame]="status() === 'ready' && kind() === 'pdf'">
        @switch (status()) {
          @case ('loading') {
            <div class="viewer-state">
              <span class="spinner-border"></span>
              <div>Loading {{ attachment()?.filename }}…</div>
            </div>
          }
          @case ('error') {
            <div class="viewer-state">
              <div class="mac-alert mac-alert--danger">
                <span class="icon"><i class="bi bi-exclamation-triangle-fill"></i></span>
                <div class="tx"><div class="ms">{{ error() }}</div></div>
              </div>
            </div>
          }
          @case ('ready') {
            @switch (kind()) {
              @case ('pdf') {
                <iframe class="viewer-frame" [src]="frameUrl()" [title]="attachment()?.filename ?? 'Document'"></iframe>
              }
              @case ('image') {
                <div class="viewer-image"><img [src]="objectUrl()" [alt]="attachment()?.filename ?? ''" /></div>
              }
              @case ('text') {
                <pre class="viewer-text mac-mono">{{ text() }}</pre>
              }
              @default {
                <div class="viewer-state">
                  <div class="icn"><i class="bi bi-file-earmark-richtext"></i></div>
                  <div class="ti">No preview for this file type</div>
                  <div class="mac-muted mac-small">Download it to open in Word, Excel or PowerPoint.</div>
                  <button type="button" class="mac-btn mac-btn--primary mt-2" (click)="download()">
                    <i class="bi bi-download"></i>Download
                  </button>
                </div>
              }
            }
          }
        }
      </div>
      <div class="modal-footer">
        @if (attachment(); as a) {
          <span class="mac-muted mac-small mac-mono me-auto">{{ formatSize(a.size) }}</span>
        }
        <button type="button" class="mac-btn mac-btn--secondary" (click)="download()" [disabled]="status() !== 'ready'">
          <i class="bi bi-download"></i>Download
        </button>
        <button type="button" class="mac-btn mac-btn--primary" data-bs-dismiss="modal">Close</button>
      </div>
    </mac-modal>
  `,
  styles: [`
    /* The viewer can open on top of another modal (e.g. Edit interaction);
       keep it above that modal. Its backdrop is handled in styles.scss. */
    :host ::ng-deep .modal { z-index: 1065; }
    /* Fill the viewport height so a PDF gets real reading space. */
    :host ::ng-deep .modal-dialog { height: calc(100% - 1rem); }
    @media (min-width: 576px) { :host ::ng-deep .modal-dialog { height: calc(100% - 3.5rem); } }
    :host ::ng-deep .modal-content { height: 100%; }
    :host ::ng-deep .modal-title { min-width: 0; overflow-wrap: anywhere; }
    :host ::ng-deep .modal-header .btn-close { flex-shrink: 0; }
    .viewer-body { flex: 1 1 auto; min-height: 0; overflow: auto; }
    .viewer-body--frame { padding: 0; overflow: hidden; }
    .viewer-frame { display: block; width: 100%; height: 100%; border: 0; }
    .viewer-image { min-height: 100%; display: grid; place-items: center; }
    .viewer-image img { max-width: 100%; max-height: 100%; object-fit: contain; }
    .viewer-text { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 12.5px; }
    .viewer-state {
      height: 100%; display: flex; flex-direction: column;
      align-items: center; justify-content: center; gap: 8px;
      text-align: center; color: var(--mac-text-muted);
      overflow-wrap: anywhere;
    }
    .viewer-state .icn { font-size: 40px; color: var(--mac-primary); }
    .viewer-state .ti { font-weight: 600; color: var(--mac-text); }
  `],
})
export class AttachmentViewerComponent implements OnInit, AttachmentViewerHost {
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly service = inject(AttachmentViewerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modal = viewChild.required(ModalComponent);

  protected readonly attachment = signal<AttachmentRecord | null>(null);
  protected readonly status = signal<ViewerStatus>('idle');
  protected readonly kind = signal<PreviewKind>('unsupported');
  protected readonly objectUrl = signal<string | null>(null);
  protected readonly frameUrl = signal<SafeResourceUrl | null>(null);
  protected readonly text = signal<string | null>(null);
  protected readonly error = signal<string | null>(null);

  private request?: Subscription;
  /** Bumped on every open so a slow earlier fetch can't overwrite a newer one. */
  private generation = 0;

  ngOnInit(): void {
    this.service.register(this);
    this.destroyRef.onDestroy(() => {
      this.service.unregister(this);
      this.reset();
    });
  }

  open(attachment: AttachmentRecord): void {
    this.reset();
    const generation = ++this.generation;
    this.attachment.set(attachment);
    this.status.set('loading');
    this.modal().show();

    const url = resolveUrl(attachment.path);
    const local = url.startsWith('blob:') || url.startsWith('data:');
    this.request = this.http.get(url, { responseType: 'blob', withCredentials: !local }).subscribe({
      next: (blob) => {
        if (generation !== this.generation) return;
        void this.present(generation, attachment, blob);
      },
      error: (err: unknown) => {
        if (generation !== this.generation) return;
        this.error.set(viewerErrorMessage(err instanceof HttpErrorResponse ? err.status : -1));
        this.status.set('error');
      },
    });
  }

  private async present(generation: number, attachment: AttachmentRecord, blob: Blob): Promise<void> {
    const kind = previewKindFor(blob.type || attachment.contentType, attachment.filename);
    const text = kind === 'text' ? await readTextPreview(blob) : null;
    if (generation !== this.generation) return;

    const url = URL.createObjectURL(blob);
    this.objectUrl.set(url);
    this.kind.set(kind);
    this.text.set(text);
    this.frameUrl.set(kind === 'pdf' ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null);
    this.status.set('ready');
  }

  protected download(): void {
    const url = this.objectUrl();
    const attachment = this.attachment();
    if (!url || !attachment) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  protected onClosed(): void {
    this.reset();
    // Bootstrap drops body.modal-open whenever any modal closes. When the
    // viewer was stacked on another modal, put it back so the page stays
    // scroll-locked behind the modal that is still open.
    if (document.querySelector('.modal.show')) document.body.classList.add('modal-open');
  }

  protected formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private reset(): void {
    this.generation += 1;
    this.request?.unsubscribe();
    this.request = undefined;
    const url = this.objectUrl();
    if (url) URL.revokeObjectURL(url);
    this.objectUrl.set(null);
    this.frameUrl.set(null);
    this.text.set(null);
    this.error.set(null);
    this.kind.set('unsupported');
    this.status.set('idle');
    this.attachment.set(null);
  }
}
